/**
 * Zentraler Katalog-Loader.
 * SINGLE source of truth: alle Produktdaten kommen aus /api/public/products.
 * Kein Fallback auf statische Produktdaten.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let _cache = null;
let _promise = null;
const _listeners = new Set();

export function productImage(slugOrProduct) {
  if (!slugOrProduct) return "";
  if (typeof slugOrProduct === "object") {
    const url = slugOrProduct.main_image_url || (slugOrProduct.images?.[0]?.url);
    if (!url) return "";
    return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
  }
  return `${BACKEND_URL}/api/product-image/${slugOrProduct}`;
}

export function invalidateCatalog() {
  _cache = null;
  _promise = null;
  _listeners.forEach((fn) => fn(null));
  if (_listeners.size) _load().catch(() => {});
}

async function _load() {
  if (_cache) return _cache;
  if (_promise) return _promise;
  _listeners.forEach((fn) => fn(null));
  _promise = api.get("/public/products").then((r) => {
    _cache = r.data || [];
    _listeners.forEach((fn) => fn(_cache));
    return _cache;
  }).catch((e) => {
    _listeners.forEach((fn) => fn(null, e));
    throw e;
  }).finally(() => { _promise = null; });
  return _promise;
}

export async function fetchProducts() {
  return _load();
}

export async function fetchProduct(slug) {
  const list = await _load();
  const local = list.find((p) => p.slug === slug);
  if (local) return local;
  // Direkter Fallback
  const r = await api.get(`/public/products/${slug}`);
  return r.data;
}

/** Reactiver Hook: liefert immer den aktuellen Katalog. */
export function useCatalog() {
  const [state, setState] = useState({ products: _cache || [], loading: !_cache, error: null });
  const [revision, setRevision] = useState(0);
  const retry = useCallback(() => {
    setState({ products: [], loading: true, error: null });
    setRevision((value) => value + 1);
  }, []);
  useEffect(() => {
    let mounted = true;
    const cb = (data, error = null) => {
      if (mounted) setState({ products: data || [], loading: data === null && !error, error });
    };
    _listeners.add(cb);
    _load()
      .then((d) => mounted && setState({ products: d, loading: false, error: null }))
      .catch((e) => mounted && setState({ products: [], loading: false, error: e }));
    return () => { mounted = false; _listeners.delete(cb); };
  }, [revision]);
  return { ...state, retry };
}

/** Einzelnes Produkt laden. */
export function useProduct(slug) {
  const [state, setState] = useState({ product: null, loading: true, error: null });
  const [revision, setRevision] = useState(0);
  const retry = useCallback(() => {
    setState({ product: null, loading: true, error: null });
    setRevision((value) => value + 1);
  }, []);
  useEffect(() => {
    let mounted = true;
    setState({ product: null, loading: true, error: null });
    fetchProduct(slug)
      .then((p) => mounted && setState({ product: p, loading: false, error: null }))
      .catch((e) => mounted && setState({ product: null, loading: false, error: e }));
    return () => { mounted = false; };
  }, [slug, revision]);
  return { ...state, retry };
}

/** Kategorien-Ableitung aus dem tatsächlichen Katalog. */
export function useCategories() {
  const { products } = useCatalog();
  const map = new Map();
  products.forEach((p) => {
    if (p.category && !map.has(p.category)) {
      map.set(p.category, { slug: p.category, label: p.category_label || p.category });
    }
  });
  return Array.from(map.values());
}
